import { useState, useEffect, useRef, useCallback } from 'react';
import { chatsApi } from '../../../api/chats';
import { normalizeReactions } from '../../../utils/reactions';

/**
 * Хук управления сообщениями чата: загрузка, пагинация, синхронизация, polling.
 *
 * @param {Object} params
 * @param {string} params.id — ID чата
 * @param {string} [params.currentProfileId] — ID профиля текущего пользователя
 * @param {boolean} params.wsConnected — подключён ли WebSocket
 * @param {React.RefObject} params.messagesContainerRef — ref контейнера сообщений (для scroll restore + IntersectionObserver)
 * @param {React.RefObject} params.topSentinelRef — ref sentinel-элемента для бесконечного скролла
 * @returns {Object} состояние сообщений, рефы и методы
 */
/**
 * Сравнение массивов приглашений по id + status (без JSON.stringify).
 */
function attachmentsEqual(a, b) {
    const arrA = a || [];
    const arrB = b || [];
    if (arrA.length !== arrB.length) return false;
    return arrA.every((inv, i) =>
        inv?.id === arrB[i]?.id && inv?.status === arrB[i]?.status
    );
}

/**
 * Сравнение массивов реакций по emoji + userId (без JSON.stringify).
 */
function reactionsEqual(a, b) {
    const arrA = a || [];
    const arrB = b || [];
    if (arrA.length !== arrB.length) return false;
    return arrA.every((r, i) =>
        r?.emoji === arrB[i]?.emoji && (r?.userId || r?.profileId) === (arrB[i]?.userId || arrB[i]?.profileId)
    );
}

/**
 * Сравнение seenBy: массив строк (UUID) или объектов с id/userId.
 */
function seenByEqual(a, b) {
    const arrA = a || [];
    const arrB = b || [];
    if (arrA.length !== arrB.length) return false;
    const toId = (s) => typeof s === 'string' ? s : (s?.id || s?.userId || s?.profileId);
    const idsB = new Set(arrB.map(toId));
    return arrA.every(s => idsB.has(toId(s)));
}

export function useChatMessages({ id, currentProfileId, wsConnected, messagesContainerRef, topSentinelRef }) {
    const [chat, setChat] = useState(null);
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [messagesPage, setMessagesPage] = useState(0);
    const [hasMoreMessages, setHasMoreMessages] = useState(true);
    const [chatParticipants, setChatParticipants] = useState([]);
    const [loadingMore, setLoadingMore] = useState(false);

    // Refs
    const abortControllerRef = useRef(null);
    const pollingIntervalRef = useRef(null);
    const loadingMoreRef = useRef(false);
    const scrollRestoreRef = useRef(null);
    const isFirstLoadRef = useRef(true);
    const totalPagesRef = useRef(1);
    const pendingMessagesRef = useRef([]);
    const firstUnreadMessageIdRef = useRef(null);
    const lastMessageCountRef = useRef(0);
    const is429Ref = useRef(false);

    /**
     * Проверка, прочитано ли сообщение текущим профилем.
     * seenBy может быть массивом UUID (WS) или массивом объектов (REST).
     */
    const isMessageRead = useCallback((msg) => {
        if (!currentProfileId || !msg) return true;
        if (msg._pending) return true;
        const seenBy = msg.seenBy;
        if (!seenBy || !Array.isArray(seenBy) || seenBy.length === 0) return false;
        return seenBy.some(s => {
            const seenId = typeof s === 'string' ? s : (s?.id || s?.userId || s?.profileId);
            return seenId === currentProfileId;
        });
    }, [currentProfileId]);

    /**
     * Утилита: извлекает массив сообщений из ответа API.
     * Сервер может вернуть Page-объект или плоский массив.
     * @returns {{ messages: Array, totalPages: number|null }}
     */
    const extractMessages = (response) => {
        if (Array.isArray(response)) {
            return { messages: response, totalPages: null };
        }
        if (response && typeof response === 'object') {
            return {
                messages: response.content || response.data || response.messages || [],
                totalPages: response.totalPages || null,
            };
        }
        return { messages: [], totalPages: null };
    };

    /**
     * Утилита: дедупликация + сортировка ASC + нормализация реакций.
     */
    const normalizeMessages = (rawMessages) =>
        rawMessages.filter((msg, index, self) =>
            index === self.findIndex((m) => m.id === msg.id)
        ).sort((a, b) =>
            new Date(a.createdAt || a.sentAt) - new Date(b.createdAt || b.sentAt)
        ).map(msg => ({
            ...msg,
            reactions: normalizeReactions(msg.reactions)
        }));

    const loadMessages = async (reset = false) => {
        try {
            // Загружаем с сортировкой DESC (новые сверху).
            // page 0 = самые новые сообщения — не нужно вычислять последнюю страницу.
            const direction = 'DESC';

            if (reset) {
                const response = await chatsApi.getMessages(
                    id,
                    { page: 0, size: 50, sortBy: 'createdAt', direction },
                    abortControllerRef.current?.signal
                );

                const { messages: rawMessages, totalPages: tp } = extractMessages(response);
                const totalPages = tp ?? 1;
                totalPagesRef.current = totalPages;

                const uniqueMessages = normalizeMessages(rawMessages);

                setMessages(uniqueMessages);
                // Следующая страница для подгрузки старых сообщений
                setMessagesPage(1);
                setHasMoreMessages(totalPages > 1);
                return uniqueMessages;
            } else {
                // Подгрузка старых сообщений: page увеличивается (DESC, page 0 = новые)
                const page = messagesPage;
                if (page < 0 || page >= totalPagesRef.current) return [];

                const response = await chatsApi.getMessages(
                    id,
                    { page, size: 50, sortBy: 'createdAt', direction },
                    abortControllerRef.current?.signal
                );

                const { messages: rawMessages, totalPages: tp } = extractMessages(response);
                if (tp != null) {
                    totalPagesRef.current = tp;
                }

                const uniqueMessages = normalizeMessages(rawMessages);

                // Prepend старых сообщений перед существующими
                setMessages(prev => {
                    const existingIds = new Set(prev.map(m => m.id));
                    const toAdd = uniqueMessages.filter(m => !existingIds.has(m.id));
                    return [...toAdd, ...prev];
                });

                const nextPage = page + 1;
                setMessagesPage(nextPage);
                setHasMoreMessages(nextPage < totalPagesRef.current);
                return uniqueMessages;
            }
        } catch (err) {
            if (err.name === 'AbortError' || err.code === 'ERR_CANCELED') {
                return [];
            }
            console.error('[useChatMessages] Ошибка загрузки сообщений:', err);
            return [];
        }
    };

    const fetchChatData = async () => {
        setLoading(true);
        setError(null);
        try {
            const chatResponse = await chatsApi.getChat(id, abortControllerRef.current?.signal);
            setChat(chatResponse);
            setChatParticipants(chatResponse.participantIds || chatResponse.participants || []);

            // Сбрасываем счетчик сообщений при смене чата
            lastMessageCountRef.current = 0;

            // Загружаем сообщения с пагинацией (reset — новый чат)
            const loadedMessages = await loadMessages(true);

            // Прокручиваем к первому непрочитанному ТОЛЬКО если в чате есть
            // непрочитанные (unreadCount > 0). Иначе при каждом открытии чата
            // старое сообщение без seenBy будет считаться «непрочитанным».
            const chatUnreadCount = chatResponse?.unreadCount || 0;
            if (currentProfileId && chatUnreadCount > 0 && loadedMessages.length > 0) {
                const firstUnread = loadedMessages.find(msg => !isMessageRead(msg));
                if (firstUnread) {
                    firstUnreadMessageIdRef.current = firstUnread.id;
                }
            } else {
                firstUnreadMessageIdRef.current = null;
            }

            // Отмечаем сообщения как прочитанные при открытии чата
            if (currentProfileId) {
                try {
                    await chatsApi.markAsReadForProfile(id, currentProfileId);
                    setMessages(prev => prev.map(msg => {
                        if (msg._pending) return msg;
                        const seenBy = msg.seenBy || [];
                        const alreadyRead = seenBy.some(s => {
                            const seenId = typeof s === 'string' ? s : (s?.id || s?.userId || s?.profileId);
                            return seenId === currentProfileId;
                        });
                        if (alreadyRead) return msg;
                        return { ...msg, seenBy: [...seenBy, currentProfileId] };
                    }));
                    // Очищаем ref — сообщения помечены прочитанными,
                    // больше не нужно прокручивать к «непрочитанному»
                    firstUnreadMessageIdRef.current = null;
                } catch (err) {
                    // markAsRead не критичен — silently ignore
                }
            }
        } catch (err) {
            if (err.name === 'AbortError' || err.code === 'ERR_CANCELED') {
                return;
            }
            setError(err.message || 'Ошибка при загрузке чата');
        } finally {
            setLoading(false);
        }
    };

    // Сброс состояния при смене чата
    useEffect(() => {
        setChat(null);
        setMessages([]);
        setMessagesPage(0);
        setHasMoreMessages(true);
        setChatParticipants([]);
        setError(null);
        is429Ref.current = false;
        loadingMoreRef.current = false;
        setLoadingMore(false);
        isFirstLoadRef.current = true;
        scrollRestoreRef.current = null;
        totalPagesRef.current = 1;
        pendingMessagesRef.current = [];
        firstUnreadMessageIdRef.current = null;
        lastMessageCountRef.current = 0;

        abortControllerRef.current = new AbortController();
        fetchChatData();

        return () => {
            abortControllerRef.current?.abort();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    /**
     * Подгрузка старых сообщений при скролле вверх.
     * Сохраняет позицию скролла для восстановления после prepend.
     */
    const loadMoreMessages = useCallback(async () => {
        if (loadingMoreRef.current || !hasMoreMessages || loading) return;

        loadingMoreRef.current = true;
        setLoadingMore(true);

        const container = messagesContainerRef.current;
        if (container) {
            scrollRestoreRef.current = {
                prevScrollHeight: container.scrollHeight,
                prevScrollTop: container.scrollTop
            };
        }

        await loadMessages(false);

        loadingMoreRef.current = false;
        setLoadingMore(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasMoreMessages, loading]);

    // IntersectionObserver: срабатывает когда sentinel сверху попадает в зону видимости
    useEffect(() => {
        if (!hasMoreMessages || loading || messages.length === 0) return;

        const sentinel = topSentinelRef.current;
        const container = messagesContainerRef.current;
        if (!sentinel || !container) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && !loadingMoreRef.current) {
                    loadMoreMessages();
                }
            },
            { root: container, rootMargin: '50px', threshold: 0 }
        );

        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [hasMoreMessages, loading, messages.length, loadMoreMessages, messagesContainerRef, topSentinelRef]);

    /**
     * Синхронизация сообщений через REST API.
     * Загружает последние сообщения, добавляет новые и обновляет существующие
     * (статус приглашений, реакции, редакции, удаления).
     */
    const syncMessages = useCallback(async () => {
        if (is429Ref.current) return;

        try {
            // DESC: page 0 = самые новые сообщения (как при initial load)
            const response = await chatsApi.getMessages(
                id,
                { page: 0, size: 50, sortBy: 'createdAt', direction: 'DESC' },
                abortControllerRef.current?.signal
            );

            const { messages: newMessages, totalPages: tp } = extractMessages(response);
            if (tp != null) {
                totalPagesRef.current = tp;
            }

            setMessages(prev => {
                const existingMap = new Map(prev.map(m => [m.id, m]));
                let changed = false;

                newMessages.forEach(msg => {
                    const existing = existingMap.get(msg.id);
                    if (existing) {
                        // Нормализуем реакции: один пользователь — одна реакция
                        const msgNormalized = {
                            ...msg,
                            reactions: normalizeReactions(msg.reactions)
                        };
                        const merged = { ...existing, ...msgNormalized };
                        // Preserve client-side poll data — server doesn't return
                        // type: 'POLL' because poll messages are sent as regular text.
                        // Without this, syncMessages overwrites type: 'POLL' and polls
                        // disappear from the message list.
                        if (existing.type === 'POLL') {
                            merged.type = 'POLL';
                            if (!merged.pollId) merged.pollId = existing.pollId;
                            if (!merged.poll) merged.poll = existing.poll;
                        }
                        // Preserve forwarded message metadata — REST API may not
                        // return forwardedFrom* fields, causing the "Переслано от"
                        // label to disappear after sync.
                        if (existing.forwardedFromMessageId && !merged.forwardedFromMessageId) {
                            merged.forwardedFromMessageId = existing.forwardedFromMessageId;
                            merged.forwardedFromSenderName = existing.forwardedFromSenderName;
                            merged.forwardedFromSender = existing.forwardedFromSender;
                        }
                        // Сравнение конкретных полей вместо JSON.stringify
                        const hasChanges =
                            existing.isDeleted !== merged.isDeleted
                            || existing.content !== merged.content
                            || !attachmentsEqual(existing.invitationAttachments, merged.invitationAttachments)
                            || !reactionsEqual(existing.reactions, merged.reactions)
                            || !seenByEqual(existing.seenBy, merged.seenBy);
                        if (hasChanges) {
                            existingMap.set(msg.id, merged);
                            changed = true;
                        }
                    } else {
                        existingMap.set(msg.id, {
                            ...msg,
                            reactions: normalizeReactions(msg.reactions)
                        });
                        changed = true;
                    }
                });

                // Если есть pending-сообщения и сервер вернул новые сообщения —
                // пытаемся заменить pending на реальные.
                // Проверяем по senderId, а при несовпадении — по содержимому
                // (сервер может вернуть senderId в другом формате).
                const pendingEntries = prev.filter(m => m._pending);
                if (pendingEntries.length > 0 && newMessages.length > 0) {
                    const myProfileId = currentProfileId;
                    // Реальные сообщения от нас, пришедшие с сервера
                    const myNewMessages = newMessages.filter(msg => {
                        const senderId = msg.senderId
                            || (typeof msg.sender === 'object'
                                ? (msg.sender.id || msg.sender.userId || msg.sender.profileId)
                                : null);
                        return senderId === myProfileId;
                    });

                    // Fallback: если senderId не совпал, ищем по содержимому
                    let messagesToMatch = myNewMessages;
                    if (myNewMessages.length === 0) {
                        messagesToMatch = newMessages.filter(msg => {
                            const msgContent = msg.content || msg.text || '';
                            return pendingEntries.some(p => p.content === msgContent);
                        });
                    }

                    if (messagesToMatch.length > 0) {
                        // Заменяем pending-сообщения на реальные (по одному)
                        let replaceIdx = 0;
                        const toRemove = new Set();
                        for (const pending of pendingEntries) {
                            if (replaceIdx < messagesToMatch.length) {
                                toRemove.add(pending.id);
                                replaceIdx++;
                            }
                        }
                        if (toRemove.size > 0) {
                            pendingMessagesRef.current = pendingMessagesRef.current
                                .filter(p => !toRemove.has(p.tempId));
                            existingMap.forEach((val, key) => {
                                if (toRemove.has(key)) {
                                    existingMap.delete(key);
                                }
                            });
                            changed = true;
                        }
                    }
                }

                if (!changed) return prev;

                return Array.from(existingMap.values()).sort((a, b) =>
                    new Date(a.createdAt || a.sentAt) - new Date(b.createdAt || b.sentAt)
                );
            });
        } catch (err) {
            if (err.name === 'AbortError' || err.code === 'ERR_CANCELED') {
                return;
            }
            if (err.response?.status === 429) {
                console.warn('[useChatMessages] Получен 429 — polling отключён');
                is429Ref.current = true;
                if (pollingIntervalRef.current) {
                    clearInterval(pollingIntervalRef.current);
                    pollingIntervalRef.current = null;
                }
                return;
            }
        }
    }, [id]);

    const syncMessagesRef = useRef(syncMessages);
    useEffect(() => {
        syncMessagesRef.current = syncMessages;
    }, [syncMessages]);

    const hasPendingInvites = useCallback(() => {
        return messages.some(msg => {
            return (msg.invitationAttachments || []).some(
                att => att.inviteType === 'BATTLE_INVITE'
                    && att.inviteId
                    && (!att.status || att.status === 'PENDING')
            );
        });
    }, [messages]);

    const hasPendingInvitesRef = useRef(hasPendingInvites);
    useEffect(() => {
        hasPendingInvitesRef.current = hasPendingInvites;
    }, [hasPendingInvites]);

    const hasPendingMessages = useCallback(() => {
        return pendingMessagesRef.current.length > 0;
    }, []);

    const hasPendingMessagesRef = useRef(hasPendingMessages);
    useEffect(() => {
        hasPendingMessagesRef.current = hasPendingMessages;
    }, [hasPendingMessages]);

    /**
     * Polling новых сообщений через REST API (fallback)
     * Работает только когда WebSocket не подключён, чтобы избежать 429.
     * При подключённом WS — периодически синхронизируем сообщения только если
     * есть pending-приглашения.
     */
    useEffect(() => {
        if (!id) return;
        if (is429Ref.current) return;

        let inviteSyncInterval = null;

        if (wsConnected) {
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
                pollingIntervalRef.current = null;
            }

            inviteSyncInterval = setInterval(() => {
                if (hasPendingInvitesRef.current() || hasPendingMessagesRef.current()) {
                    syncMessagesRef.current();
                }
            }, 15000);
        } else {
            pollingIntervalRef.current = setInterval(() => {
                syncMessages();
            }, 10000);
        }

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                if (!wsConnected || hasPendingInvitesRef.current() || hasPendingMessagesRef.current()) {
                    if (!is429Ref.current) {
                        syncMessagesRef.current();
                    }
                }
            }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
                pollingIntervalRef.current = null;
            }
            if (inviteSyncInterval) {
                clearInterval(inviteSyncInterval);
            }
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [id, syncMessages, wsConnected]);

    return {
        // State
        chat, setChat,
        messages, setMessages,
        loading,
        error, setError,
        chatParticipants, setChatParticipants,
        hasMoreMessages,
        loadingMore,
        messagesPage, setMessagesPage,
        setHasMoreMessages,
        // Refs
        abortControllerRef,
        pendingMessagesRef,
        totalPagesRef,
        is429Ref,
        isFirstLoadRef,
        scrollRestoreRef,
        loadingMoreRef,
        firstUnreadMessageIdRef,
        lastMessageCountRef,
        pollingIntervalRef,
        // Methods
        isMessageRead,
        fetchChatData,
        loadMessages,
        loadMoreMessages,
        syncMessages,
    };
}
